import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Search, MessageCircle } from "lucide-react";
import { useT } from "@/lib/i18n";
import { api, errMsg, mediaUrl, fmtPrice } from "@/lib/api";

const SPEC_FIELDS = ["brand", "model", "reference", "variant", "budget", "deadline"];

const effPrice = (w) => (w.price_on_request || !w.price_cents ? null : w.promo_price_cents || w.price_cents);

function WatchPicker({ watches, value, onChange, testid }) {
  const { t, lang } = useT();
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("");
  const [av, setAv] = useState("");
  const [sort, setSort] = useState("recent");
  const brands = useMemo(() => [...new Set(watches.map((w) => w.brand))].sort(), [watches]);
  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    let l = watches.filter((w) => (!brand || w.brand === brand) && (!av || w.availability === av) && (!term || `${w.brand} ${w.model} ${w.reference || ""}`.toLowerCase().includes(term)));
    if (sort !== "recent") l = [...l].sort((a, b) => (effPrice(a) ?? Infinity) * (sort === "price_desc" ? -1 : 1) - (effPrice(b) ?? Infinity) * (sort === "price_desc" ? -1 : 1));
    return l;
  }, [watches, q, brand, av, sort]);
  const selected = watches.find((w) => w.id === value);
  return (
    <div data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.25em] text-[#A39E93] mb-3">{t.f_choose} *</p>
      <div className="grid gap-3 sm:grid-cols-[1.6fr_1fr_1fr_1fr] mb-4">
        <label className="relative"><Search className="w-4 h-4 absolute left-0 top-1/2 -translate-y-1/2 text-[#A39E93]" />
          <input className="field !pl-6" placeholder={t.picker_search} value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-search`} /></label>
        <select className="field" value={brand} onChange={(e) => setBrand(e.target.value)} data-testid={`${testid}-brand`}><option value="">{t.brand}: {t.all}</option>{brands.map((b) => <option key={b} value={b}>{b}</option>)}</select>
        <select className="field" value={av} onChange={(e) => setAv(e.target.value)} data-testid={`${testid}-availability`}><option value="">{t.availability}: {t.all}</option>{["in_stock", "on_order"].map((k) => <option key={k} value={k}>{t.avail[k]}</option>)}</select>
        <select className="field" value={sort} onChange={(e) => setSort(e.target.value)} data-testid={`${testid}-sort`}><option value="recent">{t.sort_recent}</option><option value="price_asc">{t.sort_price_asc}</option><option value="price_desc">{t.sort_price_desc}</option></select>
      </div>
      <p className="text-[11px] text-[#A39E93] mb-3" data-testid={`${testid}-count`}>{t.results(list.length)}{selected ? ` · ${t.picker_selected}: ${selected.brand} ${selected.model}` : ""}</p>
      <div className="grid sm:grid-cols-2 gap-3 max-h-[440px] overflow-y-auto pr-1" role="radiogroup" aria-label={t.f_choose}>
        {list.map((w) => {
          const on = value === w.id;
          return (
            <button type="button" key={w.id} role="radio" aria-checked={on} onClick={() => onChange(on ? "" : w.id)} data-testid={`${testid}-option-${w.slug}`}
              className={`flex items-center gap-4 p-2.5 pr-4 text-left border transition-[border-color,background-color] duration-300 ${on ? "border-[#C8AE6E] bg-[#C8AE6E]/10" : "border-[#282520] hover:border-[#C8AE6E]/40"}`}>
              <span className="photo-frame w-16 h-20 shrink-0">
                {w.image ? <img src={mediaUrl(w.image.sm)} alt={`${w.brand} ${w.model}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" /> : <img src="/emblem-square.png" alt="" className="absolute inset-0 m-auto w-1/2 opacity-30" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[9px] uppercase tracking-[0.25em] text-[#C8AE6E]">{w.brand}</span>
                <span className="block font-display text-[15px] text-[#F3EFE6] leading-tight truncate">{w.model}</span>
                <span className="block text-[11px] text-[#A39E93] mt-1">{t.avail[w.availability]} · <span className="text-[#E6D5AC]">{effPrice(w) ? fmtPrice(effPrice(w), lang) : t.on_request}</span></span>
              </span>
              <span className={`w-4 h-4 rounded-full border shrink-0 ${on ? "border-[#C8AE6E] bg-[#C8AE6E] shadow-[inset_0_0_0_3px_#0C0B09]" : "border-[#A39E93]/50"}`} />
            </button>
          );
        })}
        {list.length === 0 && <p className="text-sm text-[#A39E93] py-6" data-testid={`${testid}-empty`}>{t.picker_empty}</p>}
      </div>
    </div>
  );
}

export function RequestSuccess({ data, testid }) {
  const { t, r } = useT();
  return (
    <div className="py-8 text-center reveal" data-testid={`${testid}-success`}>
      <CheckCircle2 className="w-10 h-10 mx-auto text-[#C8AE6E]" strokeWidth={1} />
      <h3 className="mt-4 text-2xl text-[#F3EFE6]">{t.f_done_title}</h3>
      <p className="mt-2 text-sm text-[#A39E93]">{t.f_ref}: <span className="text-[#E6D5AC] font-medium tracking-wider" data-testid={`${testid}-ref`}>{data.ref}</span></p>
      <p className="mt-3 text-sm text-[#A39E93] max-w-sm mx-auto">{t.f_done}</p>
      <div className="mt-6 flex flex-wrap gap-3 justify-center">
        <a href={data.whatsapp_url} target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid={`${testid}-whatsapp`}><MessageCircle className="w-4 h-4" /> {t.f_send_wa}</a>
        <Link to={`${r.track}?ref=${data.ref}`} className="btn-line" data-testid={`${testid}-track`}>{t.track_cta}</Link>
      </div>
    </div>
  );
}

export function RequestForm({ type, prefill = {}, source = "site", conversationId = null, watches = null, onDone, compact = false, testid = "request-form", openWhatsApp = true }) {
  const { t, lang, r } = useT();
  const started = useRef(Date.now() / 1000);
  const [f, setF] = useState({ visit_date: "", visit_time: "", name: prefill.name || "", phone: "", salutation: prefill.salutation || "", message: prefill.message || "", watch_id: prefill.watch_id || "", website: "", consent: false, specific: { ...Object.fromEntries(SPEC_FIELDS.map((k) => [k, ""])), ...(prefill.specific || {}) } });
  const [mode, setMode] = useState(type === "reservation" ? "reservation" : "purchase");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const setSp = (k) => (e) => setF({ ...f, specific: { ...f.specific, [k]: e.target.value } });
  const picked = watches?.find((w) => w.id === f.watch_id);
  const watchAv = type === "catalog" ? picked?.availability : null;
  const canToggle = type === "purchase" || type === "reservation" || watchAv === "in_stock";
  const effType = type === "catalog" ? (watchAv === "on_order" ? "order" : mode) : canToggle ? mode : type;
  const needsWatch = ["purchase", "reservation", "order", "catalog", "waitlist"].includes(type);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (needsWatch && !f.watch_id) return setErr(t.f_choose);
    if (type === "visit" && (!f.visit_date || !f.visit_time)) return setErr(t.visit_hint);
    if (f.phone.replace(/\D/g, "").length < 9) return setErr(t.f_need_phone);
    if (!f.consent) return setErr(t.f_need_consent);
    setBusy(true);
    const popup = openWhatsApp ? window.open("", "_blank") : null;
    try {
      const { data } = await api.post("/public/requests", { ...f, type: effType, lang, source, conversation_id: conversationId, started_at: started.current, watch_id: f.watch_id || null, specific: effType === "specific" ? f.specific : null, visit_date: type === "visit" ? f.visit_date : null, visit_time: type === "visit" ? f.visit_time : null });
      if (popup) popup.location.href = data.whatsapp_url;
      setDone(data);
      onDone?.(data);
    } catch (e2) {
      popup?.close();
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  if (done) return <RequestSuccess data={done} testid={testid} />;

  const grid = compact ? "grid gap-4" : "grid gap-6 sm:grid-cols-2";
  return (
    <form onSubmit={submit} className="space-y-6" data-testid={testid} noValidate>
      {needsWatch && (watches ? <WatchPicker watches={watches} value={f.watch_id} onChange={(id) => setF({ ...f, watch_id: id })} testid={`${testid}-watch-picker`} />
        : prefill.watch_label && <p className="text-sm text-[#E6D5AC] border-l border-[#C8AE6E] pl-3" data-testid={`${testid}-watch-label`}>{prefill.watch_label}</p>)}
      {canToggle && (
        <div className="flex flex-wrap gap-2" role="radiogroup" data-testid={`${testid}-mode`}>
          {["purchase", "reservation"].map((k) => (
            <button type="button" key={k} role="radio" aria-checked={mode === k} onClick={() => setMode(k)} data-testid={`${testid}-mode-${k}`}
              className={`px-4 py-2 rounded-full text-xs tracking-wide border transition-colors duration-300 ${mode === k ? "border-[#C8AE6E] bg-[#C8AE6E]/15 text-[#E6D5AC]" : "border-[#282520] text-[#A39E93] hover:text-[#F3EFE6]"}`}>{t[`mode_${k}`]}</button>
          ))}
          <p className="w-full text-[11px] text-[#A39E93]">{t[`mode_${mode}_hint`]}</p>
        </div>
      )}
      {type === "waitlist" && <p className="text-[11px] text-[#A39E93]" data-testid={`${testid}-waitlist-hint`}>{t.mode_waitlist_hint}</p>}
      {watchAv === "on_order" && <p className="text-[11px] text-[#A39E93]" data-testid={`${testid}-order-hint`}>{t.mode_order_hint}</p>}
      {type === "visit" && (
        <div className="space-y-3" data-testid={`${testid}-visit`}>
          {prefill.watch_label && <p className="text-sm text-[#E6D5AC] border-l border-[#C8AE6E] pl-3">{prefill.watch_label}</p>}
          <p className="text-[11px] text-[#A39E93]">{t.visit_hint}</p>
          <div className="grid grid-cols-2 gap-4">
            <label><span className="text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">{t.f_visit_date} *</span>
              <input type="date" className="field [color-scheme:dark]" min={new Date(Date.now() + 864e5).toISOString().slice(0, 10)} value={f.visit_date} onChange={set("visit_date")} data-testid={`${testid}-visit-date`} /></label>
            <label><span className="text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">{t.f_visit_time} *</span>
              <select className="field" value={f.visit_time} onChange={set("visit_time")} data-testid={`${testid}-visit-time`}><option value="">—</option>
                {["10:00", "10:30", "11:00", "11:30", "12:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30", "18:00", "18:30"].map((h) => <option key={h} value={h}>{h}</option>)}</select></label>
          </div>
        </div>
      )}
      {type === "contact" && prefill.watch_label && <p className="text-sm text-[#E6D5AC] border-l border-[#C8AE6E] pl-3">{prefill.watch_label}</p>}
      {type === "specific" && (
        <div className={grid}>
          {SPEC_FIELDS.map((k) => <input key={k} className="field" placeholder={t[`f_${k}`] + (k === "brand" ? " *" : "")} value={f.specific[k]} onChange={setSp(k)} data-testid={`${testid}-specific-${k}`} maxLength={200} />)}
        </div>
      )}
      <div className={grid}>
        <input className="field" placeholder={`${t.f_name} *`} value={f.name} onChange={set("name")} required data-testid={`${testid}-name`} maxLength={120} />
        <input className="field" type="tel" placeholder={`${t.f_phone} *`} value={f.phone} onChange={set("phone")} required data-testid={`${testid}-phone`} maxLength={40} />
        <select className="field" value={f.salutation} onChange={set("salutation")} data-testid={`${testid}-salutation`}>
          <option value="">{t.f_salutation}</option>
          {Object.entries(t.sal).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <textarea className="field min-h-[90px] resize-y" placeholder={t.f_message} value={f.message} onChange={set("message")} data-testid={`${testid}-message`} maxLength={3000} />
      <input type="text" name="website" value={f.website} onChange={set("website")} tabIndex={-1} autoComplete="off" className="absolute -left-[9999px] w-px h-px opacity-0" aria-hidden="true" />
      <label className="flex items-start gap-3 text-xs text-[#A39E93] leading-relaxed cursor-pointer">
        <input type="checkbox" checked={f.consent} onChange={set("consent")} className="mt-0.5 accent-[#C8AE6E] w-4 h-4 shrink-0" data-testid={`${testid}-consent`} />
        <span>{t.f_consent} <Link to={r.privacy} className="underline hover:text-[#E6D5AC]" target="_blank">{t.privacy}</Link></span>
      </label>
      {err && <p className="text-sm text-[#e0937f]" role="alert" data-testid={`${testid}-error`}>{err}</p>}
      <button type="submit" className="btn-gold w-full sm:w-auto" disabled={busy} data-testid={`${testid}-submit`}><MessageCircle className="w-4 h-4" /> {busy ? t.f_sending : t[`send_${effType}`] || t.f_send}</button>
      <p className="text-[11px] text-[#A39E93]">{openWhatsApp ? t.f_wa_note : t.f_wa_note.split(".").slice(-2).join(".").trim()}</p>
    </form>
  );
}
